var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/x-data-view-table.ts
import { RXController } from "@adornis/chemistry/controllers/RXController.js";
import { css } from "@adornis/chemistry/directives/css.js";
import { virtualize } from "@adornis/chemistry/directives/when-seen.js";
import "@adornis/chemistry/elements/components/x-empty-placeholder.js";
import "@adornis/chemistry/elements/components/x-flex.js";
import "@adornis/chemistry/elements/components/x-grid.js";
import { DesignSystem } from "@adornis/chemistry/elements/theming/design.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import "@adornis/dragdrop/x-drag-drop.js";
import { FieldConfiguration, makeUniqueIDForFieldConf } from "@adornis/field-management/field-configuration.js";
import { translateFieldName } from "@adornis/field-management/translate-field.js";
import { changeStream } from "@adornis/filter/change-stream.js";
import "@adornis/popover/x-dropdown.js";
import { getScrollParent } from "@adornis/shadowroot-helpers/get-scroll-parent.js";
import { TranslationController } from "@adornis/translation-core/client/translation-controller.js";
import { html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import { filter, fromEvent, map, switchMap, takeUntil } from "rxjs";
import { getFieldFromEntity } from "../get-field-from-entity.js";
import { XDataViewAbstractView } from "./x-data-view-abstract-view.js";
import "./x-sorting-card.js";
var XTableView = class extends XDataViewAbstractView {
  constructor() {
    super(...arguments);
    this._mouseMoveIndex = -1;
    this._mouseMoveStartX = 0;
    this._mouseClientX = 0;
    this._draggingClippedOnMin = false;
    this._translate = new TranslationController(this);
    this.rerenderPlease = new RXController(
      this,
      changeStream(
        this.fullOptions.observable.pipe(
          filter(Boolean),
          switchMap((options) => options.view.whenChanging),
          map((v) => v.toObject())
        )
      ).pipe(
        filter(Boolean),
        filter((ch) => ch.some((ch2) => ch2.concernsPath(["selectedItems"]) || ch2.concernsPath(["configuredColumns"])))
      )
    );
    this.finalColumns = [];
    this.gridRef = createRef();
    this.renderedIndexCallback = (index) => {
    };
  }
  styles() {
    const styles = [
      {
        "[row] [active]": { background: this.colors.primary.shade(200) },
        [`[row]:not(:last-child) > ${DesignSystem.prefix}-flex`]: {
          borderBottom: `1px solid ${this.colors.neutral.shade(50)}`
        },
        ".header-text": { background: this.colors.neutral.shade(50), transition: "all 0.125s ease-in" },
        ".handle:hover": { background: this.colors.primary.shade(100) },
        ".no-select": {
          "-webkit-touch-callout": "none",
          "-webkit-user-select": "none",
          "user-select": "none"
        },
        ".column-selector": { background: this.colors.neutral.shade(50) },
        ".border": {
          zIndex: "2",
          background: `${this.colors.neutral} !important`,
          width: "3px",
          marginInline: "0 auto",
          transition: "all 0.125s ease-in",
          marginBottom: "-1px"
        }
      }
    ];
    if (this.options.isEntryClickable)
      styles.push({
        "[row] > *": { cursor: "pointer" },
        [`[row]:hover > .cell`]: {
          background: `${this.colors.primary.shade(100)} !important`,
          zIndex: "1"
        }
      });
    return styles;
  }
  willUpdate(chp) {
    super.willUpdate(chp);
    if (this.fullOptions.value)
      this.finalColumns = this.fullOptions.value.finalColumns();
  }
  get columnString() {
    return this.finalColumns.map((def) => `${def.columnWidth ?? "150px"}${this.options?.isColumnResizable ? " 3px" : ""}`).join(" ");
  }
  _updateColumns() {
    if (!this.gridRef.value) {
      return;
    }
    const widths = this.gridRef.value.columns.split(" ").filter((_, index) => index % 2 === 0);
    const width = widths[this._mouseMoveIndex];
    if (width === void 0) {
      console.error("Column dragging has no value!");
      return;
    }
    const borderDragging = this.shadowRoot?.getElementById(this._mouseMoveIndex.toString() + "border");
    const borderPosition = borderDragging?.getBoundingClientRect().x;
    if (borderPosition === void 0)
      return;
    if (this._draggingClippedOnMin && this._mouseClientX < borderPosition) {
      this._mouseMoveStartX = borderPosition;
      return;
    }
    const gridHeader = this.shadowRoot?.getElementById(this._mouseMoveIndex.toString());
    const containerWidth = gridHeader?.getBoundingClientRect().width ?? 30;
    const pointerRelativeXpos = this._mouseClientX - this._mouseMoveStartX;
    let newContainerWidth = containerWidth + pointerRelativeXpos;
    this._mouseMoveStartX = this._mouseClientX;
    const boxAminWidth = 30;
    if (newContainerWidth < boxAminWidth) {
      newContainerWidth = boxAminWidth;
      this._draggingClippedOnMin = true;
    } else {
      this._draggingClippedOnMin = false;
    }
    widths[this._mouseMoveIndex] = newContainerWidth + "px";
    this.gridRef.value.columns = widths.map((w) => `${w} 3px`).join(" ");
  }
  connectedCallback() {
    super.connectedCallback();
    fromEvent(document, "mousemove").pipe(takeUntil(this.disconnected)).subscribe((e) => {
      if (this._mouseMoveIndex === -1)
        return;
      this._mouseClientX = e.clientX;
      this._updateColumns();
      e.preventDefault();
    });
    fromEvent(document, "mouseup").pipe(takeUntil(this.disconnected)).subscribe((_) => {
      if (this._mouseMoveIndex === -1)
        return;
      this._mouseMoveIndex = -1;
      if (!this.options?.view)
        throw new Error("need view here");
      const widths = this.gridRef.value?.columns.split(" ").filter((_2, index) => index % 2 === 0) ?? [];
      this.finalColumns?.forEach((colDef, index) => {
        const confCol = this.fullOptions.value?.view.configuredColumns?.find(
          (cc) => makeUniqueIDForFieldConf(cc) === makeUniqueIDForFieldConf(colDef)
        );
        if (!confCol)
          return;
        if (confCol.columnWidth !== widths[index])
          confCol.columnWidth = widths[index];
      });
    });
    const scrollParent = getScrollParent(this);
    if (scrollParent)
      scrollParent.style.overflowAnchor = "none";
  }
  render() {
    if (!this.options?.isColumnResortable)
      return this._table();
    return xComponents(html`
      <x-drag-drop
        .degrees-on-move=${0}
        drag-mode="row"
        child-selector=${".handle"}
        @resorted=${async (e) => {
      if (!this.options?.view)
        return;
      this.options.view.configuredColumns = this.options.view.configuredColumns?.sort(
        (a, b) => e.detail.order.indexOf(FieldConfiguration.resolveID(a)) - e.detail.order.indexOf(FieldConfiguration.resolveID(b))
      );
    }}
      >
        ${this._table()}
      </x-drag-drop>
    `);
  }
  _table() {
    if (!this.options)
      throw new Error("Options are missing in data view table");
    const columnDefinitions = this.finalColumns;
    return xComponents(html`
      <x-grid columns=${this.columnString} ${ref(this.gridRef)}>
        ${this.headerLine()}
        ${this.data.length ? html`
              ${repeat(
      this.data,
      (entityInstance) => this.resolveID(entityInstance),
      (entityInstance, indexRow) => html`
                    ${virtualize(
        html`
                        ${repeat(
          columnDefinitions,
          (colDef) => colDef,
          (colDef, indexColumn) => xComponents(
            html` ${this._renderField(entityInstance, colDef, indexColumn, indexRow)}
                              ${this.options?.isColumnResizable ? this._fieldBorder(indexColumn, false) : nothing}`
          )
        )}
                      `,
        (oldHeight) => html` <div ${css({ height: `${oldHeight}px`, gridColumn: "1 / -1" })}></div> `,
        {
          wrapperElem: () => this.virtualizationWrapper(entityInstance, indexRow),
          wrapperDisplayContents: true,
          onRender: () => this.renderedIndexCallback(indexRow)
        }
      )}
                  `
    )}
            ` : !this.loading ? this.options.emptyState() : nothing}
        ${this.loading ? html`<x-flex center ${css({ gridColumn: "1 / -1" })}> ${this.options.loader()} </x-flex> ` : nothing}
      </x-grid>
    `);
  }
  _fieldBorder(index, isHeader) {
    const columns = this.finalColumns;
    const colDefBefore = columns.at(index);
    const colDefAfter = columns.at(index + 1);
    if (!colDefBefore)
      throw new Error("Cannot attach border to non existent column");
    let leftOffset = "0px";
    let rightOffset = "0px";
    if (colDefBefore.isSticky)
      leftOffset = colDefBefore.position === "front" ? parseInt(this._getOffsetForStackedStickyColumns(colDefBefore, index + 1, "left").replace("px", "")) - 3 + "px" : "0px";
    if (colDefAfter?.isSticky)
      rightOffset = parseInt(this._getOffsetForStackedStickyColumns(colDefAfter, index, "right").replace("px", "")) - 3 + "px";
    const style = {
      position: isHeader || colDefBefore.isSticky || colDefAfter?.position === "back" && colDefAfter.isSticky ? "sticky" : "unset",
      top: isHeader ? "0" : "unset",
      // headers remain on top so they can get dragged if the user is further down the list. A sticky columns border is above them so it doesnt scroll over headers
      zIndex: isHeader && colDefBefore.isSticky ? "111" : isHeader ? "11" : "1",
      left: colDefBefore.isSticky ? leftOffset : "unset",
      right: colDefAfter?.position === "back" ? rightOffset : "unset",
      cursor: colDefBefore.isResizable && isHeader ? "col-resize" : "auto"
    };
    if (index === columns.length - 1 && !colDefBefore?.isResizable)
      return html`<x-flex ${css(style)}></x-flex>`;
    if (!isHeader)
      return html`<x-flex class="border" ${css(style)}></x-flex>`;
    return xComponents(html`
      <x-flex
        id=${index + "border"}
        class="border"
        ${css(style)}
        @mousedown=${(e) => {
      if (!colDefBefore.isResizable)
        return;
      this._mouseMoveIndex = index;
      this._mouseMoveStartX = e.clientX;
    }}
      ></x-flex>
    `);
  }
  // TODO this isn't being executed anyways, clean up
  columnHeaderClicked(indexOfColumn) {
    if (!this.options)
      throw new Error("missing options");
    const columnDefinition = this.finalColumns[indexOfColumn];
    if (this.options.isColumnHeaderClickable) {
      const event = new CustomEvent("column-header-clicked", {
        detail: { columnDefinition },
        composed: true
      });
      this.dispatchEvent(event);
    }
    if (!this.options.isSortable || !columnDefinition?.fieldName || columnDefinition.isSortable === false)
      return;
    this.dispatchEvent(
      new CustomEvent("sort", {
        detail: { columnIndex: indexOfColumn, columnFieldName: columnDefinition.fieldName, sortingDirection: "desc" }
      })
    );
  }
  headerLine() {
    const options = this.options;
    if (!options)
      throw new Error("options missing");
    if (!options.shouldRenderHeader)
      return nothing;
    const columnDefinitions = this.finalColumns;
    if (!columnDefinitions.length)
      return nothing;
    return html`
      <div style="display: contents">
        ${repeat(
      columnDefinitions,
      (colDef) => colDef._id,
      (colDef, index) => {
        const leftOffset = colDef.isSticky && colDef.position === "front" ? this._getOffsetForStackedStickyColumns(colDef, index, "left") : "0px";
        const rightOffset = colDef.isSticky && colDef.position === "back" ? this._getOffsetForStackedStickyColumns(colDef, index, "right") : "0px";
        const style = {
          position: "sticky",
          top: "0",
          zIndex: colDef.isSticky ? "111" : "1",
          [colDef.position === "back" ? "right" : "left"]: colDef.isSticky ? colDef.position === "back" ? rightOffset : leftOffset : "unset",
          background: colDef.customID === "columnSelector" ? this.colors.accent : ""
        };
        if (colDef.renderHeader) {
          return xComponents(html`
                <x-flex
                  key=${colDef._id}
                  id=${index}
                  class="header-text no-select ${colDef.position ?? !options?.isColumnResortable ? "" : "handle"}"
                  crossaxis-center
                  horizontal
                  ${css(style)}
                  @click=${() => this.columnHeaderClicked(index)}
                >
                  ${colDef.renderHeader(this)} ${this._sortingIconDisplay(colDef)}
                </x-flex>
                ${this.options?.isColumnResizable ? this._fieldBorder(index, true) : nothing}
              `);
        }
        const dropdownRef = createRef();
        const items = this._getHeaderContextMenuItems(colDef, () => dropdownRef.value?.close());
        let columnName = "";
        if (colDef.columnName !== null) {
          columnName = colDef.columnName;
        } else if (colDef.fieldName) {
          if (options.showFullFieldPath) {
            columnName = translateFieldName(colDef.fieldName);
          } else {
            columnName = translateFieldName(colDef.fieldName).split(" > ").at(-1) ?? "";
          }
        }
        if (!items.length)
          return xComponents(html`
                <x-flex
                  h100
                  crossaxis-center
                  horizontal
                  padding="xs"
                  id=${index}
                  key=${colDef._id}
                  ${css(style)}
                  class="header-text no-select ${colDef.position ?? !options?.isColumnResortable ? "" : "handle"}"
                >
                  <x-text bold ellipsis @click=${() => this.columnHeaderClicked(index)} .value=${columnName}> </x-text>
                </x-flex>
                ${this.options?.isColumnResizable ? this._fieldBorder(index, true) : nothing}
              `);
        return xComponents(html`
              <x-dropdown
                ${ref(dropdownRef)}
                class="header-text no-select ${colDef.position ?? !options?.isColumnResortable ? "" : "handle"}"
                id=${index}
                key=${colDef._id}
                ${css(style)}
              >
                <x-flex h100 crossaxis-center horizontal padding="xs" @click=${() => dropdownRef.value?.open()}>
                  <x-text
                    bold
                    ellipsis
                    ${css({
          cursor: (options.isSortable || options.isColumnHeaderClickable) && colDef.fieldName && colDef.isSortable !== false ? "pointer" : "auto"
        })}
                    @click=${() => this.columnHeaderClicked(index)}
                    .value=${columnName}
                  >
                  </x-text>
                  ${this._sortingIconDisplay(colDef)}
                </x-flex>
                <x-basic-card slot="dropdown-content" ${css({ padding: "0" })}> ${items} </x-basic-card>
              </x-dropdown>
              ${this.options?.isColumnResizable ? this._fieldBorder(index, true) : nothing}
            `);
      }
    )}
      </div>
    `;
  }
  _getHeaderContextMenuItems(colDef, close) {
    const buttonStyle = {
      padding: this.spacing.xs,
      borderBottom: `1px solid ${this.colors.neutral.shade(100)}`,
      borderRadius: "0"
    };
    const items = [
      this.options.isSortable && (colDef.fieldName || colDef.isSortable === true) ? html`
            <x-button
              ${css(buttonStyle)}
              mode="text"
              icon="sort"
              @click=${() => {
        const filter2 = this.fullOptions.value?.view.filter;
        if (!filter2)
          throw new Error("this cant happen");
        this.dispatchEvent(
          new CustomEvent("sort", {
            detail: {
              columnFieldName: colDef.fieldName ?? colDef.customID,
              sortingDirection: "asc"
            }
          })
        );
        close();
      }}
            >
              ${this._translate.translate("data-view", "sort")}
            </x-button>
          ` : null,
      // TODO support filter adding in columns. This is disabled for now
      // ? possible problem. The blacklist for the fields in the filter editor could be more restrictive than the fieldselection in the dataview. How can we onyl enable this if the item is not it the blacklist of the filter?
      // colDef.fieldName || !!colDef.filterAspect // TODO also allow generic filters
      //   ? html`
      //       <x-button
      //         ${css(buttonStyle)}
      //         mode="text"
      //         icon="add"
      //         @click=${() => {
      //           // TODO also support choosing arbitrary types
      //           // ? that should probably be done by accessing a static method on the filter picker and spawning the dialog or something
      //           // the issue is that multiple aspects are viable and figuring out what's already overridden is very hard from this point
      //           if (!colDef.filterAspect) return;
      //           this.options.view.filter?.aspects.push(
      //             // TODO need filter options here
      //             constructFilterAspect(colDef.filterAspect.aspectConstructor, colDef),
      //           );
      //           close();
      //         }}
      //       >
      //         ${this._translate.translate('filter', 'addFilter')}
      //       </x-button>
      //     `
      //   : null,
      !colDef.isAlwaysVisible && this.options.isColumnSelectable ? html`
            <x-button
              ${css(buttonStyle)}
              mode="text"
              icon="delete"
              @click=${() => {
        this.options.view.configuredColumns?.splice(
          this.options.view.configuredColumns.findIndex(
            (confCol) => FieldConfiguration.resolveID(confCol) === colDef._id
          ),
          1
        );
        close();
      }}
            >
              ${this._translate.translate("data-view", "delete")}
            </x-button>
          ` : null
    ].filter((v) => v);
    return items;
  }
  _sortingIconDisplay(colDef) {
    if (!this.options)
      throw new Error("options missing");
    const filter2 = this.options.view?.filter;
    if (!this.options.isSortable || !filter2)
      return nothing;
    const sortingInformationIndex = filter2.sortingFields.findIndex((si) => si === (colDef.customID ?? colDef.fieldName));
    if (sortingInformationIndex === -1)
      return nothing;
    return html`
      <x-sorting-card
        .sortingDirection=${filter2.sortingDirections[sortingInformationIndex]}
        .sortingNumber=${sortingInformationIndex}
        @closed=${(e) => {
      this.dispatchEvent(
        new CustomEvent("sort", {
          detail: {
            columnIndex: sortingInformationIndex,
            columnFieldName: colDef.customID ?? colDef.fieldName,
            sortingDirection: void 0
          }
        })
      );
    }}
        @updated=${(e) => {
      this.dispatchEvent(
        new CustomEvent("sort", {
          detail: {
            columnIndex: sortingInformationIndex,
            columnFieldName: filter2.sortingFields[sortingInformationIndex],
            sortingDirection: e.detail.sortingDirection
          }
        })
      );
    }}
        @click=${(e) => e.stopPropagation()}
      ></x-sorting-card>
    `;
  }
  _renderField(entityInstance, colDef, indexColumn, indexRow) {
    const options = this.options;
    if (!options)
      throw new Error("options missing");
    const leftOffset = colDef.isSticky && colDef.position === "front" ? this._getOffsetForStackedStickyColumns(colDef, indexColumn, "left") : "0px";
    const rightOffset = colDef.isSticky && colDef.position === "back" ? this._getOffsetForStackedStickyColumns(colDef, indexColumn, "right") : "0px";
    const href = this.options.rowHref(entityInstance);
    const content = colDef.renderItem ? colDef.renderItem(entityInstance, this, indexRow) : this.fallbackRenderItem(getFieldFromEntity(entityInstance, colDef));
    return html`
      <x-flex
        padding="xs"
        center
        class="cell ${colDef.customID === "columnSelector" ? "column-selector" : ""}"
        ${css({
      position: colDef.isSticky ? "sticky" : "unset",
      [colDef.position === "back" ? "right" : "left"]: colDef.isSticky ? colDef.position === "back" ? rightOffset : leftOffset : "unset",
      zIndex: colDef.isSticky ? "10" : "unset"
    })}
        ?active=${this.options?.view?.selectedItems?.includes(this.resolveID(entityInstance))}
        @click=${() => {
      if (options.isEntryClickable) {
        this.options?.view?.toggleSelection(
          entityInstance.constructor.resolveID(entityInstance)
        );
        this.dispatchEvent(
          new CustomEvent("entry-clicked", {
            detail: { entity: entityInstance, column: colDef },
            bubbles: true,
            composed: true
          })
        );
      }
    }}
      >
        ${href && !colDef.disabledHref ? html`
              <a
                href="${href}"
                ${css({
      textDecoration: "none",
      color: "inherit",
      cursor: "pointer"
    })}
              >
                ${content}
              </a>
            ` : colDef.customID === "columnSelector" ? html`<div style="opacity: 0"></div>` : content}
      </x-flex>
    `;
  }
  _getOffsetForStackedStickyColumns(colDef, colIndex, direction) {
    if (!colDef.isSticky)
      return "0px";
    const columnsToDirection = direction === "left" ? this.finalColumns.slice(0, colIndex) : this.finalColumns.slice(colIndex + 1);
    const offset = columnsToDirection.reduce((acc, curr) => acc + +curr.columnWidth.replace("px", "") + 3, 0);
    return offset.toString() + "px";
  }
};
__decorateClass([
  state()
], XTableView.prototype, "_translate", 2);
__decorateClass([
  state()
], XTableView.prototype, "rerenderPlease", 2);
XTableView = __decorateClass([
  customElement("x-data-view-table")
], XTableView);
export {
  XTableView
};
